import { Suspense } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { RouteFallback } from '@/components/ui/RouteFallback'
import type { Database } from '@jac/shared/generated/db-types'
import { usePrincipals, useSession } from '@/hooks/useSession'
import { useStickySearch } from '@/lib/stickyParams'
import { pt } from '@/i18n/pt-PT'
import { cn } from '@/lib/cn'

// AdminLayout is now a tab strip rendered INSIDE the operations shell — the
// outer sidebar + topbar are owned by OperationsLayout. Tabs deep-link via
// real route paths so back/forward + bookmarks work.

type Role = Database['public']['Enums']['user_role']
// platformOnly: visible only to the PLATFORM OPERATOR (platform_admins) —
// Analytics reads cross-firm aggregates, so other firms must not see it.
type Tab = { to: string; label: string; end?: boolean; roles?: Role[]; platformOnly?: boolean }

// The old "Resumo" landing tab is gone (2026-08-25): its counts were redundant, the
// needs-attention and routing-gaps lists became filterable columns on Clientes, and the
// platform settings moved to Notificações. /admin now lands on Clientes directly.
const tabs: Tab[] = [
  {
    to: '/admin/analytics',
    label: pt.operations.adminTabs.analytics,
    roles: ['admin'],
  },
  // Roberto's interaction log sits next to Analytics: it is the same kind of
  // object (a reporting view), and admin-only for the same reason — it contains
  // clients' own questions and the supplier names and amounts in the answers.
  // Contabilidade por fornecedor — reporting sobre classificações contabilísticas,
  // admin-only como a Analytics (agregado firm-wide via definer RPC).
  {
    to: '/admin/contabilidade',
    label: pt.operations.adminTabs.contabilidade,
    roles: ['admin'],
  },
  {
    to: '/admin/roberto',
    label: pt.operations.adminTabs.roberto,
    roles: ['admin'],
  },
  // Novidades — what we choose to tell clients. Next to the other admin-only
  // surfaces, and admin-only for the same kind of reason: publishing one speaks
  // to every client of the firm at once.
  {
    to: '/admin/novidades',
    label: pt.operations.adminTabs.novidades,
    roles: ['admin'],
  },
  {
    to: '/admin/escritorios',
    label: pt.operations.adminTabs.firms,
    roles: ['admin'],
    platformOnly: true,
  },
  { to: '/admin/clients', label: pt.operations.adminTabs.clients },
  { to: '/admin/onboarding', label: pt.operations.adminTabs.onboarding },
  {
    to: '/admin/fornecedores',
    label: pt.operations.adminTabs.suppliers,
    roles: ['admin', 'team_lead'],
  },
  { to: '/admin/utilizadores', label: pt.operations.adminTabs.clientUsers, roles: ['admin'] },
  { to: '/admin/consultants', label: pt.operations.adminTabs.consultants, roles: ['admin'] },
  { to: '/admin/risco', label: pt.operations.adminTabs.risco, roles: ['admin'] },
  { to: '/admin/learning', label: pt.operations.adminTabs.learning, roles: ['admin', 'team_lead'] },
  { to: '/admin/notificacoes', label: pt.operations.adminTabs.notificacoes, roles: ['admin'] },
]

export function AdminLayout() {
  const session = useSession()
  const { isPlatformAdmin } = usePrincipals()
  const role = session.status === 'authenticated' ? session.consultant.role : null
  // Platform tabs belong to the platform OPERATOR, not to whoever is admin of the JA firm.
  const visible = tabs.filter(
    (t) => (!t.roles || (role && t.roles.includes(role))) && (!t.platformOnly || isPlatformAdmin),
  )
  const stickySearch = useStickySearch()

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h1 className="text-[20px] font-semibold tracking-tight text-gray-900">Administração</h1>
        <p className="mt-1 text-[12.5px] text-gray-500">
          Configuração da plataforma, gestão de clientes e equipa.
        </p>
      </header>

      <div className="flex items-stretch gap-1 border-b border-DEFAULT">
        {visible.map((t) => (
          <NavLink
            key={t.to}
            to={`${t.to}${stickySearch}`}
            end={t.end}
            className={({ isActive }) =>
              cn(
                '-mb-px inline-flex items-center px-4 py-2.5 text-[13.5px] transition-colors',
                'border-b-[1.5px]',
                isActive
                  ? 'border-brand-800 font-medium text-brand-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>

      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </div>
  )
}
